import { Languages } from 'lucide-react';
import { useContext, useEffect, useState, type ReactNode } from 'react';

import { isTranslationEnabled } from '../services/translationApi';

import {
  TranslateAllContext,
  type FillOutcome,
  type Filler,
  type Registry,
} from './translateAllContext';

/** A form whose bilingual fields one "Translate all" button fills together. */
export function TranslateAllProvider({ children }: { children: ReactNode }) {
  const [registry] = useState<Registry>(() => {
    const fillers = new Set<Filler>();
    return {
      add: (fill) => {
        fillers.add(fill);
        return () => {
          fillers.delete(fill);
        };
      },
      all: () => [...fillers],
    };
  });
  return <TranslateAllContext.Provider value={registry}>{children}</TranslateAllContext.Provider>;
}

/** A few at a time, so a long form does not fire every request at once. */
async function runPooled(fillers: Filler[], size = 3): Promise<FillOutcome[]> {
  const out: FillOutcome[] = new Array(fillers.length);
  let next = 0;
  const worker = async () => {
    while (next < fillers.length) {
      const i = next++;
      out[i] = await fillers[i]().catch((): FillOutcome => 'failed');
    }
  };
  await Promise.all(Array.from({ length: Math.min(size, fillers.length) }, worker));
  return out;
}

/**
 * "Translate all": every field with text in one language gets the other one, where
 * that side is empty or was machine-translated. Text a person wrote is never
 * replaced. Shown only while translation is on.
 */
export function TranslateAllButton({
  className = '',
  disabled = false,
}: {
  className?: string;
  /** While something else is filling the form (smart intake). */
  disabled?: boolean;
}) {
  const registry = useContext(TranslateAllContext);
  const [enabled, setEnabled] = useState(false);
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    let live = true;
    void isTranslationEnabled().then((on) => live && setEnabled(on));
    return () => {
      live = false;
    };
  }, []);

  if (!registry || !enabled) return null;

  const run = async () => {
    setBusy(true);
    setMessage(null);
    const outcomes = await runPooled(registry.all());
    const filled = outcomes.filter((o) => o === 'filled').length;
    const failed = outcomes.filter((o) => o === 'failed').length;
    setMessage(
      failed
        ? `${filled} translated. ${failed} could not be translated; fill those in yourself or try again.`
        : filled
          ? `${filled} translated. Please look them over.`
          : 'Nothing to translate: every field already has both languages.'
    );
    setBusy(false);
  };

  return (
    <div className={`flex flex-wrap items-center gap-3 ${className}`}>
      <button
        type="button"
        onClick={() => void run()}
        disabled={busy || disabled}
        className="inline-flex items-center gap-1.5 rounded-lg border border-primary px-3 py-1.5 text-sm font-medium text-primary hover:bg-primary/5 disabled:opacity-50"
      >
        <Languages className="h-4 w-4" aria-hidden="true" />
        {busy ? 'Translating...' : 'Translate all'}
      </button>
      {message && (
        <span role="status" className="text-sm text-gray-600">
          {message}
        </span>
      )}
    </div>
  );
}
